import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  Typeahead,
  TypeaheadInputMulti,
  Token,
} from "react-bootstrap-typeahead";

import { useUiFeedback } from "../../../../context/ui-feedback/ui-feedback";
import {
  createWaiting,
  closeWaiting,
  createErrorFeedback,
} from "../../../../context/ui-feedback/action";
import { useSearchWorkers } from "../../../../api/users-api";
import { Loading } from "../../../common/loading/loading";

export const WorkersSelect = ({
  exludedWorkers,
  workers,
  isValid,
  isInvalid,
  disabled,
  handleWorkersSelect,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const searchWorkers = useSearchWorkers();

  const { uiFeedbackDispatch } = useUiFeedback();

  const loadedUsers = useRef(null);
  const [allUsers, setAllUsers] = useState(null);
  const [users, setUsers] = useState(null);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    setSelected(
      workers.map((worker) => {
        return {
          id: worker.id,
          label: worker.name,
          user: worker,
        };
      }),
    );
  }, [workers]);

  useEffect(() => {
    if (allUsers !== null) {
      const exludedWorkerIds = exludedWorkers.map((ew) => ew.id);
      setUsers(allUsers.filter((u) => !exludedWorkerIds.includes(u.id)));
    }
  }, [exludedWorkers, allUsers]);

  useEffect(() => {
    const loadUsers = async () => {
      createWaiting(uiFeedbackDispatch);
      try {
        await searchWorkers((data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            let userData = data.map((u) => {
              return {
                id: u.id,
                label: u.name,
                user: u,
              };
            });
            userData.sort((u1, u2) => u1.label.localeCompare(u2.label));
            setAllUsers(userData);
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.editserviceworksheet.exception.loaduser.title",
            }),
            intl.formatMessage({
              id: "page.editserviceworksheet.exception.loaduser.description",
            }),
          );
        }
      } finally {
        closeWaiting(uiFeedbackDispatch);
      }
    };

    const checkNotEmpy = (items) => {
      return items.filter((item) => item === null).length === 0;
    };

    if (
      loadedUsers.current === null &&
      users === null &&
      checkNotEmpy([searchWorkers, intl, navigate, uiFeedbackDispatch])
    ) {
      loadUsers();
      loadedUsers.current = new Date();
    }
  }, [loadedUsers, users, searchWorkers, intl, navigate, uiFeedbackDispatch]);

  const onRemove = (event) => {
    handleWorkersSelect([
      ...selected.filter((s) => s.id !== event.id).map((s) => s.user),
    ]);
  };

  if (!users) {
    return <Loading />;
  }

  return (
    <Typeahead
      id="service-workers"
      multiple
      onChange={(event) => {
        handleWorkersSelect([...event.map((s) => s.user)]);
      }}
      options={users}
      placeholder={intl.formatMessage({
        id: "data.serviceworksheet.form.workers.value.null",
      })}
      emptyLabel={intl.formatMessage({
        id: "data.serviceworksheet.form.workers.emptyLabel",
      })}
      renderInput={(inputProps) => (
        <TypeaheadInputMulti
          {...inputProps}
          isValid={isValid}
          isInvalid={isInvalid}
          selected={selected}
          disabled={disabled}
        >
          {selected.map((user, _) => (
            <Token
              key={user.id}
              onRemove={onRemove}
              option={user}
              disabled={disabled}
            >
              {user.label}
            </Token>
          ))}
        </TypeaheadInputMulti>
      )}
      selected={selected}
    />
  );
};

WorkersSelect.propTypes = {
  exludedWorkers: PropTypes.array.isRequired,
  workers: PropTypes.array,
  isValid: PropTypes.bool.isRequired,
  isInvalid: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  handleWorkersSelect: PropTypes.func.isRequired,
};
